import { useEffect, useRef, useState } from 'react';
import type { SentItem } from '../types';
import { PMS } from '@/features/shared/pms';

const SENT_BADGE: Record<SentItem['status'], { label: string; cls: string }> = {
  waiting: { label: 'Waiting', cls: 'b-info' },
  late: { label: 'Late', cls: 'b-warn' },
  asked_more: { label: 'Asked for more', cls: 'b-warn' },
  denied: { label: 'Denied', cls: 'b-bad' },
};

interface Props {
  item: SentItem;
  resending: boolean;
  onResend: () => void;
  onBack: () => void;
  /** Denial "what now" — resolved inline on the page, never a modal (Oct 18 fix). */
  onDecide?: (choiceId: string) => void;
}

/**
 * Read-only view of a sent pre-auth with its status history (Oct 17 fix:
 * /pre-auths/PA-S2 used to say "isn't waiting"). "Review and resend" rows get a
 * primary "Resend to {payer}" button; nothing shows an internal id. A denied
 * item with a decision pending shows its "what now" options inline on the page
 * (Oct 18 fix: Jorge's missing-tooth denial — options inline, not a modal).
 */
export function SentDetail({ item, resending, onResend, onBack, onDecide }: Props) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [choice, setChoice] = useState('');
  useEffect(() => { headingRef.current?.focus(); setChoice(''); }, [item.id]);
  const badge = SENT_BADGE[item.status];
  const canResend = item.action === 'review_resend';
  const pendingDecision = item.action === 'decide';
  // A waiting-period or missing-tooth denial won't change by resending — ELVA
  // couldn't have known either in advance, and nothing changes by trying again
  // (Oct 17/18 fix: "Send it again" hidden for both).
  const canMoveTreatment = item.denyReason === 'waiting_period' || item.denyReason === 'frequency';
  const canResendDenial = item.denyReason !== 'waiting_period' && item.denyReason !== 'missing_tooth';
  const decideOptions = [
    ...(canMoveTreatment ? [{ id: 'move', label: 'Move the treatment to Feb 1, 2027' }] : []),
    { id: 'pay', label: `${item.patient} will pay for it` },
    ...(canResendDenial ? [{ id: 'resend', label: 'Send it again with more information' }] : []),
    { id: 'cancel', label: 'Cancel the treatment' },
  ];

  return (
    <main className="pa-detail">
      <button type="button" className="pa-back" onClick={onBack}>← All pre-auths</button>
      <div className="pa-detail-inner">
        <section className="pa-claimhead" aria-labelledby="pa-sent-name">
          <div>
            <h2 id="pa-sent-name" className="pa-patient" tabIndex={-1} ref={headingRef}>{item.patient}</h2>
            <p className="pa-chsub">{item.payerShort} · {item.code} {item.procedure}</p>
            <p className="pa-chmeta">
              Sent {item.sentDisplay} · appointment {item.appointmentDisplay}
              {item.reference ? <> · reference <span className="num">{item.reference}</span></> : null}
            </p>
          </div>
          <div className="pa-right">
            <div className="pa-meta">Status</div>
            <span className={`pa-badge ${badge.cls}`}>{badge.label}</span>
          </div>
        </section>

        <section className="pa-card" aria-labelledby="pa-sent-what">
          <h3 className="pa-h3" id="pa-sent-what">What’s happening</h3>
          <p className="pa-consequence" style={{ margin: 0 }}>{item.sentence}</p>
          <p className="pa-meta" style={{ marginTop: 8 }}>This is a read-only view. ELVA checks it until the payer answers.</p>
        </section>

        <section className="pa-card" aria-labelledby="pa-sent-hist">
          <h3 className="pa-h3" id="pa-sent-hist" style={{ marginBottom: 10 }}>Status history</h3>
          <ol className="pa-timeline" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(item.timeline ?? []).map((t, i) => (
              <li key={`tl-${String(i)}`} style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
                <span className="pa-meta num" style={{ minWidth: 56 }}>{t.when}</span>
                <span style={{ color: 'var(--fg2)' }}>{t.text}</span>
              </li>
            ))}
          </ol>
        </section>

        {canResend ? (
          <div className="pa-actionbar">
            <button type="button" className="pa-btn pa-btn-primary pa-btn-lg" disabled={resending} onClick={onResend}>
              {resending ? 'Resending…' : `Resend to ${item.payerShort}`}
            </button>
            <span className="pa-actionbar-meta">ELVA re-attached the document. Resending sends it again with everything the payer asked for.</span>
          </div>
        ) : null}

        {pendingDecision ? (
          <section className="pa-decision" aria-labelledby="pa-sent-decide-title">
            <div className="pa-decision-head">
              <span className="pa-badge b-white" style={{ background: '#fff', border: '1px solid #e9c98a', color: 'var(--warning)' }}>
                Needs your decision
              </span>
              <h3 className="pa-decision-title" id="pa-sent-decide-title">{item.payerShort} said no — what now?</h3>
            </div>
            <div className="pa-decision-body">
              <div className="pa-options" role="radiogroup" aria-labelledby="pa-sent-decide-title">
                {decideOptions.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    role="radio"
                    aria-checked={choice === o.id}
                    className="pa-option"
                    style={choice === o.id ? { background: 'var(--psoft)', borderColor: 'var(--primary)', fontWeight: 600 } : undefined}
                    onClick={() => { setChoice(o.id); }}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              {canMoveTreatment ? (
                <p className="pa-meta" style={{ margin: 0 }}>
                  Moving the treatment is done in {PMS}; ELVA follows the change and re-checks the timing.
                </p>
              ) : null}
              <div className="pa-actionbar" style={{ marginTop: 10 }}>
                <button
                  type="button"
                  className="pa-btn pa-btn-primary"
                  disabled={!choice}
                  onClick={() => { onDecide?.(choice); }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </section>
        ) : null}
      </div>
    </main>
  );
}
